export interface RequestOptions {
  headers?: Record<string, string>
  timeout?: number
  params?: Record<string, string | number | boolean>
  skipAuth?: boolean
}

export class HttpError extends Error {
  status: number
  statusText: string
  responseBody?: string

  constructor(status: number, statusText: string, message: string, responseBody?: string) {
    super(message)
    this.name = 'HttpError'
    this.status = status
    this.statusText = statusText
    this.responseBody = responseBody
  }
}

export interface IHttpClient {
  get<T>(url: string, options?: RequestOptions): Promise<T>
  post<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T>
  patch<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T>
  delete<T>(url: string, options?: RequestOptions): Promise<T>
}

interface FetchHttpClientOptions {
  baseURL: string
  getAuthToken?: () => string | null | undefined
}

export class FetchHttpClient implements IHttpClient {
  private readonly baseURL: string
  private readonly getAuthToken?: () => string | null | undefined

  constructor(options: FetchHttpClientOptions) {
    this.baseURL = options.baseURL.replace(/\/+$/, '')
    this.getAuthToken = options.getAuthToken
  }

  private buildUrl(path: string, params?: Record<string, string | number | boolean>): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    const url = new URL(`${this.baseURL}${normalizedPath}`)

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.set(key, String(value))
      })
    }

    return url.toString()
  }

  private buildHeaders(options?: RequestOptions): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options?.headers,
    }

    if (!options?.skipAuth && this.getAuthToken) {
      const token = this.getAuthToken()
      if (token && !headers.Authorization) {
        headers.Authorization = `Bearer ${token}`
      }
    }

    return headers
  }

  private async parseResponse<T>(response: Response): Promise<T> {
    const responseText = await response.text()
    const contentType = response.headers.get('content-type') || ''
    const isJson = contentType.includes('application/json')

    if (!response.ok) {
      let message = `${response.status} ${response.statusText}`
      if (isJson && responseText) {
        try {
          const parsed = JSON.parse(responseText) as { message?: string; error?: string }
          if (parsed.message) {
            message = parsed.message
          } else if (parsed.error) {
            message = parsed.error
          }
        } catch {
          // Keep default fallback message.
        }
      }
      throw new HttpError(response.status, response.statusText, message, responseText || undefined)
    }

    if (response.status === 204 || !responseText) {
      return {} as T
    }

    if (isJson) {
      return JSON.parse(responseText) as T
    }

    return {} as T
  }

  private async request<T>(
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    const url = this.buildUrl(path, options?.params)
    const headers = this.buildHeaders(options)

    const response = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: options?.timeout ? AbortSignal.timeout(options.timeout) : undefined,
    })

    return this.parseResponse<T>(response)
  }

  get<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', url, undefined, options)
  }

  post<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', url, body, options)
  }

  patch<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', url, body, options)
  }

  delete<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', url, undefined, options)
  }
}

